// BL-177 Step 2k: "Create reservation" from a conversation. THE rules, in one pure module that the browser (button, tile,
// dispatch "Needs info" rows) and the Deno Edge Function (reservation-from-conversation) both import, so what the tile
// promises and what the server enforces cannot drift apart. No dependencies: all time-zone work uses Intl.
//
// No second rule set: the required fields ARE the AI Attendant's locked Gate 5 checklist (aiAttendantEngine.js:
// CAPTURE_ALWAYS_REQUIRED_FIELDS + the conditional name / email / phone), the extraction ARE the Attendant's prompt and
// JSON envelope (buildAttendantSystemPrompt / parseClaudeAttendantReply), and the flight check IS the engine's FlightAware
// request/parse helpers. What this module adds is only what a staff-triggered booking needs on top: the conversation
// window, validity of each value (future date and time, phone, e-mail, flight number) and the Needs info bookkeeping.

import {
  CAPTURE_ALWAYS_REQUIRED_FIELDS,
  CAPTURE_CONDITIONAL_FIELDS,
  CAPTURE_SOFT_ASK_FIELDS,
  buildCaptureChecklist,
  mergeCaptureChecklist,
} from './aiAttendantEngine.js';
import { normalizeTime, zonedToInstant } from './bookingTime.js';
export { normalizeTime, zonedToInstant } from './bookingTime.js';

export const RESERVATION_WINDOW_HOURS = 48;
export const TILE_COUNTDOWN_SECONDS = 30;
/** Server grace after the 5 seconds before the sweeper treats an unanswered tile as "no response". */
export const TILE_EXPIRY_GRACE_SECONDS = 20;

/** Extra optional keys asked of the model on top of the Attendant's envelope. */
export const RESERVATION_EXTRA_EXTRACTED_FIELDS = ['vehicle', 'notes', 'airport_terminal', 'rider_names'];

export const RESERVATION_STATUS = Object.freeze({
  PENDING_REVIEW: 'pending_review',
  CREATED: 'created',
  NEEDS_INFO: 'needs_info',
  CANCELED: 'canceled',
});

export const FIELD_LABELS = Object.freeze({
  pickup_location: 'Pickup',
  dropoff_location: 'Drop-off',
  date: 'Date',
  time: 'Time',
  service_type: 'Service',
  passenger_count: 'Passengers',
  luggage_count: 'Luggage',
  full_name: 'Client name',
  email: 'Client email',
  phone: 'Client phone',
  flight_info: 'Flight number',
  vehicle: 'Vehicle',
  notes: 'Notes',
  airport_terminal: 'Airport terminal',
  rider_names: 'Rider names',
});

/** Display order of the tile and of the problem list. */
export const FIELD_ORDER = Object.freeze([
  'pickup_location', 'dropoff_location', 'date', 'time', 'passenger_count', 'luggage_count', 'flight_info',
  'service_type', 'vehicle', 'full_name', 'phone', 'email', 'notes', 'airport_terminal', 'rider_names',
]);

const HOUR = 60 * 60 * 1000;

// ---------------------------------------------------------------------------------------------
// The conversation window: messages since the last reservation for this client, or the last 48 hours, whichever is shorter
// ---------------------------------------------------------------------------------------------

/**
 * The window start as an ms instant. `lastReservationAt` is when the previous reservation from this conversation was made.
 * @param {{ now?: Date, lastReservationAt?: string|null }} [options]
 */
export function computeWindowStart({ now = new Date(), lastReservationAt = null } = {}) {
  const nowMs = now instanceof Date ? now.getTime() : Date.parse(now);
  const capMs = nowMs - RESERVATION_WINDOW_HOURS * HOUR;
  const lastMs = lastReservationAt ? Date.parse(lastReservationAt) : NaN;
  if (Number.isNaN(lastMs)) return capMs;
  return Math.max(capMs, lastMs);
}

/**
 * Only messages inside the window. `messages` are mergeThreadMessages() entries ({ at: ms|null, text, direction, ... });
 * an undated message is dropped (it cannot be placed in the window).
 */
export function messagesInWindow(messages = [], windowStartMs) {
  return (Array.isArray(messages) ? messages : []).filter((message) => (
    message && typeof message.at === 'number' && message.at >= windowStartMs && String(message.text || '').trim()
  ));
}

/**
 * The idempotency key: one reservation per conversation + window (same previous reservation + same newest message).
 * @param {{ previousReservationId?: string|null, newestMessageAt?: number|string|null }} [options]
 */
export function buildWindowKey({ previousReservationId = null, newestMessageAt = null } = {}) {
  const newest = newestMessageAt === null || newestMessageAt === undefined
    ? 'none'
    : String(typeof newestMessageAt === 'number' ? newestMessageAt : Date.parse(newestMessageAt));
  return `${previousReservationId || 'none'}:${newest}`;
}

const OUTBOUND_ROLES = ['assistant', 'staff', 'agent', 'ai'];

function entryTime(value) {
  const ms = typeof value === 'number' ? value : Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The two logs a conversation lives in (the Attendant's conversation_log and the provider's raw_message_log) as one dated,
 * de-duplicated list for the extraction. Deliberately small: the thread view's richer merge (templates, media, optimistic
 * bubbles) is for display; extraction only needs who said what, and when.
 * @param {{ conversationLog?: unknown, rawMessageLog?: unknown, emailSubject?: string }} [logs]
 * @returns {Array<{ at: number|null, text: string, direction: 'inbound'|'outbound' }>}
 */
export function threadMessagesForExtraction({ conversationLog = [], rawMessageLog = [], emailSubject = '' } = {}) {
  const out = [];
  const seen = new Set();
  const add = (direction, rawText, at) => {
    const text = typeof rawText === 'string' ? rawText.trim() : '';
    if (!text) return;
    const key = `${direction}|${text}`;
    const time = entryTime(at);
    // the same text in the same direction within a minute is the one message logged twice
    const near = out.find((m) => m.direction === direction && m.text === text && (m.at === null || time === null || Math.abs(m.at - time) < 60 * 1000));
    if (near) { if (near.at === null) near.at = time; return; }
    seen.add(key);
    out.push({ at: time, text, direction });
  };
  for (const entry of Array.isArray(conversationLog) ? conversationLog : []) {
    if (!entry || typeof entry !== 'object') continue;
    add(OUTBOUND_ROLES.includes(String(entry.role || '').toLowerCase()) ? 'outbound' : 'inbound', entry.content ?? entry.text ?? entry.body, entry.at ?? entry.created_at);
  }
  for (const entry of Array.isArray(rawMessageLog) ? rawMessageLog : []) {
    if (!entry || typeof entry !== 'object') continue;
    add(String(entry.direction || '').toLowerCase() === 'outbound' ? 'outbound' : 'inbound', [entry.subject || entry.email_subject || '', entry.body ?? entry.text].filter(Boolean).join('\n'), entry.received_at ?? entry.at ?? entry.created_at);
  }
  const firstInbound = out.find(message => message.direction === 'inbound');
  if (firstInbound && emailSubject && !firstInbound.text.includes(emailSubject)) firstInbound.text = `${emailSubject}\n${firstInbound.text}`;
  return out.sort((a, b) => (a.at === null ? 1 : b.at === null ? -1 : a.at - b.at));
}

/** The conversation as plain turns for the Attendant's Messages API call. Client = user, anyone on our side = assistant. */
export function buildExtractionMessages(messages = [], { includeMessageDates = false, timeZone = 'America/New_York' } = {}) {
  const turns = [];
  for (const message of messages) {
    const role = message.direction === 'inbound' ? 'user' : 'assistant';
    const body = String(message.text || '').trim();
    if (!body) continue;
    const date = includeMessageDates && Number.isFinite(message.at) ? new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(message.at)) : '';
    const text = date ? `[Message date: ${date}; time zone: ${timeZone}]\n${body}` : body;
    const previous = turns[turns.length - 1];
    if (previous && previous.role === role) previous.content += `\n${text}`;
    else turns.push({ role, content: text });
  }
  // The API wants the conversation to start and end with the client.
  while (turns.length && turns[0].role !== 'user') turns.shift();
  while (turns.length && turns[turns.length - 1].role !== 'user') turns.pop();
  return turns;
}

export const RESERVATION_EXTRACTION_INSTRUCTION = [
  'STAFF-TRIGGERED RESERVATION CAPTURE: a team member pressed "Create reservation" on this conversation.',
  'Read the whole conversation below and fill "extracted" with everything the CLIENT has actually said or confirmed.',
  'Dates as YYYY-MM-DD and times as 24-hour HH:MM. Flight numbers as the airline code plus digits (for example DL1234).',
  'Include "vehicle" (the vehicle or car type asked for) and "notes" (anything else the driver or dispatcher should know) when present.',
  'When explicitly supplied, include airport_terminal and rider_names. Treat the email subject as conversation context. Resolve relative dates only against the dated client message, and leave conflicting or uncertain dates blank.',
  'Never guess a field the client did not give: leave it out. The reply field is not sent to anyone; set it to a short summary.',
].join('\n');

// ---------------------------------------------------------------------------------------------
// From the model's answer to the reservation fields
// ---------------------------------------------------------------------------------------------

/**
 * The reservation fields from one extraction pass: the Attendant's own checklist merge (same keys, same "unknown keys are
 * ignored" safety) plus the optional vehicle / notes the model may have returned.
 * @returns {Record<string, string|number|null>}
 */
/** @param {{ extracted?: Record<string, unknown>, onFileFields?: string[], known?: Record<string, string> }} [options] */
export function fieldsFromExtraction({ extracted = {}, onFileFields = [], known = {} } = {}) {
  const checklist = mergeCaptureChecklist(buildCaptureChecklist({ onFileFields }), extracted);
  const fields = {};
  for (const key of FIELD_ORDER) {
    const fromChecklist = checklist[key]?.value;
    const raw = fromChecklist !== undefined && fromChecklist !== null ? fromChecklist : extracted?.[key];
    fields[key] = raw === undefined || raw === '' ? null : raw;
  }
  // Facts already on the client's file fill any gap the conversation left.
  for (const [key, value] of Object.entries(known)) {
    if (key in fields && (fields[key] === null || fields[key] === '') && value) fields[key] = value;
  }
  return fields;
}

// ---------------------------------------------------------------------------------------------
// Value parsing
// ---------------------------------------------------------------------------------------------

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
// The engine's own designator shape (aiAttendantEngine.js FLIGHT_IDENT_PATTERN: airline code, 1-4 digits, optional letter), anchored for a
// single value. IATA airline codes may carry a digit (B6, U2, 9W), which the engine's letters-only scan misses; FlightAware has the last word.
export const FLIGHT_NUMBER_PATTERN = /^(?=[A-Z0-9]*[A-Z])[A-Z0-9]{2,3}\d{1,4}[A-Z]?$/;

/** "3:30 PM" / "15:30" / "9:05am" to "HH:MM" (24 hour); null when it is not a time. */

/** True for a real calendar date written YYYY-MM-DD. */
export function isRealDate(value) {
  const match = DATE_PATTERN.exec(String(value ?? '').trim());
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, day));
  return probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day;
}

/** E.164 for a typed number: 10 digits = US/Canada. The browser/Deno callers pass libphonenumber's toE164 instead. */
export function basicToE164(value) {
  const text = String(value ?? '').trim();
  if (!text) return null;
  const digits = text.replace(/\D/g, '');
  if (text.startsWith('+')) return /^[1-9]\d{7,14}$/.test(digits) ? `+${digits}` : null;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

/** Flight number to its tidy form (DL 1234 -> DL1234); null when it does not look like a flight designator. */
export function normalizeFlightNumber(value) {
  const compact = String(value ?? '').toUpperCase().replace(/[\s-]+/g, '');
  return FLIGHT_NUMBER_PATTERN.test(compact) ? compact : null;
}

// ---------------------------------------------------------------------------------------------
// Validation: the whole reservation, field by field
// ---------------------------------------------------------------------------------------------

/**
 * @typedef {{ field: string, label: string, kind: 'missing'|'invalid', message: string }} Problem
 * @param {Record<string, unknown>} fields
 * @param {{
 *   now?: Date, timeZone?: string, onFileFields?: string[],
 *   toE164?: (value: string) => string|null,
 *   flightCheck?: { status: 'found'|'not_found'|'unverified', detail?: string }|null
 * }} [options]
 * @returns {{ ok: boolean, problems: Problem[], values: Record<string, unknown>, startsAt: string|null }}
 */
export function validateReservationFields(fields = {}, {
  now = new Date(), timeZone = 'America/New_York', onFileFields = [], toE164 = basicToE164, flightCheck = null,
} = {}) {
  const problems = [];
  const values = { ...fields };
  const problem = (field, kind, message) => problems.push({ field, label: FIELD_LABELS[field] || field, kind, message });
  const blank = (value) => value === null || value === undefined || (typeof value === 'string' && !value.trim());

  const required = [
    ...CAPTURE_ALWAYS_REQUIRED_FIELDS, 'dropoff_location',
    ...CAPTURE_CONDITIONAL_FIELDS.filter((field) => !onFileFields.includes(field)),
  ];
  for (const field of required) {
    if (blank(fields[field])) problem(field, 'missing', `${FIELD_LABELS[field]} is needed.`);
  }

  for (const field of ['pickup_location', 'dropoff_location']) {
    if (blank(fields[field])) continue;
    values[field] = String(fields[field]).trim();
    if (values[field].length < 3) problem(field, 'invalid', `${FIELD_LABELS[field]} does not look like an address.`);
  }
  if (!problems.some((p) => p.field === 'pickup_location' || p.field === 'dropoff_location')
      && !blank(fields.pickup_location) && !blank(fields.dropoff_location)
      && String(fields.pickup_location).trim().toLowerCase() === String(fields.dropoff_location).trim().toLowerCase()) {
    problem('dropoff_location', 'invalid', 'Pickup and drop-off are the same place.');
  }

  // Date + time: a real calendar date and clock time, in the future in the company's time zone.
  let startsAt = null;
  const dateText = blank(fields.date) ? '' : String(fields.date).trim();
  const timeText = blank(fields.time) ? '' : normalizeTime(fields.time);
  if (dateText && !isRealDate(dateText)) problem('date', 'invalid', 'Date is not a real date (use year-month-day).');
  if (!blank(fields.time) && !timeText) problem('time', 'invalid', 'Time is not a real time of day.');
  if (timeText) values.time = timeText;
  if (dateText && isRealDate(dateText) && timeText) {
    const instant = zonedToInstant(dateText, timeText, timeZone);
    const nowMs = now instanceof Date ? now.getTime() : Date.parse(now);
    if (instant <= nowMs) problem('date', 'invalid', 'The pickup date and time have already passed.');
    else startsAt = new Date(instant).toISOString();
  }

  for (const [field, min, max] of [['passenger_count', 1, 99], ['luggage_count', 0, 99]]) {
    if (blank(fields[field])) continue;
    const number = Number(fields[field]);
    if (!Number.isInteger(number) || number < min || number > max) problem(field, 'invalid', `${FIELD_LABELS[field]} must be a whole number from ${min} to ${max}.`);
    else values[field] = number;
  }

  if (!blank(fields.phone)) {
    const phone = toE164(String(fields.phone));
    if (!phone) problem('phone', 'invalid', 'Client phone is not a valid number.');
    else values.phone = phone;
  }
  if (!blank(fields.email)) {
    const email = String(fields.email).trim();
    if (!EMAIL_PATTERN.test(email)) problem('email', 'invalid', 'Client email is not a valid address.');
    else values.email = email.toLowerCase();
  }

  // Flight number: soft ask (never required), but when one is given it must be a real one.
  if (!blank(fields.flight_info)) {
    const flight = normalizeFlightNumber(fields.flight_info);
    if (!flight) problem('flight_info', 'invalid', 'Flight number does not look like an airline and number (for example DL1234).');
    else if (flightCheck?.status === 'not_found') problem('flight_info', 'invalid', `FlightAware has no flight ${flight}.`);
    else values.flight_info = flight;
  }

  const order = (field) => FIELD_ORDER.indexOf(field);
  problems.sort((a, b) => order(a.field) - order(b.field));
  return { ok: problems.length === 0, problems, values, startsAt };
}

/** Required-and-valid fields as a plain count, for the tile's "9 of 11 complete" line. */
export function completionCounts(fields = {}, problems = [], onFileFields = []) {
  const required = [
    ...CAPTURE_ALWAYS_REQUIRED_FIELDS, 'dropoff_location',
    ...CAPTURE_CONDITIONAL_FIELDS.filter((field) => !onFileFields.includes(field)),
  ];
  const bad = new Set(problems.map((p) => p.field));
  return { total: required.length, good: required.filter((field) => !bad.has(field)).length };
}

export const SOFT_FIELDS = Object.freeze([...CAPTURE_SOFT_ASK_FIELDS, ...RESERVATION_EXTRA_EXTRACTED_FIELDS]);

// ---------------------------------------------------------------------------------------------
// Words the rest of the product shows
// ---------------------------------------------------------------------------------------------

/** The booking notes line stored on the engagement, in the Attendant's own "Label: value | ..." style. */
export function buildReservationNotes(fields = {}, { triggeredBy = '' } = {}) {
  const parts = [];
  if (fields.passenger_count !== null && fields.passenger_count !== undefined) parts.push(`Passengers: ${fields.passenger_count}`);
  if (fields.luggage_count !== null && fields.luggage_count !== undefined) parts.push(`Luggage: ${fields.luggage_count}`);
  if (fields.flight_info) parts.push(`Flight: ${fields.flight_info}`);
  if (fields.vehicle) parts.push(`Vehicle: ${fields.vehicle}`);
  const client = [fields.full_name, fields.phone, fields.email].filter(Boolean).join(', ');
  if (client) parts.push(`Client: ${client}`);
  if (fields.notes) parts.push(`Notes: ${fields.notes}`);
  parts.push(triggeredBy ? `Created from the conversation by ${triggeredBy}.` : 'Created from the conversation.');
  return parts.join(' | ');
}

/** "Pickup, Date and Client phone" for a list of problems. */
export function describeProblemFields(problems = []) {
  const labels = problems.map((p) => p.label);
  if (labels.length <= 1) return labels.join('');
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

export function threadNoteForCreated({ confirmationNumber, byName }) {
  return `Reservation #${confirmationNumber} created${byName ? ` by ${byName}` : ''}.`;
}

export function threadNoteForNeedsInfo({ confirmationNumber, problems, byName }) {
  return `Reservation #${confirmationNumber} added to dispatch as Needs info${byName ? ` (started by ${byName})` : ''}: ${describeProblemFields(problems)} still needed. The client confirmation is on hold.`;
}

/** The link carried by the thread note and the bell alert. */
export function reservationLink(engagementId) {
  return `/dispatch?reservation=${engagementId}`;
}

/** What the client is told if the Attendant's own confirmation wording could not be generated. */
export function fallbackConfirmationText({ confirmationNumber, fields = {}, startsAtLabel = '' }) {
  const lines = [`Your reservation is confirmed${confirmationNumber ? ` (confirmation #${confirmationNumber})` : ''}.`];
  if (startsAtLabel) lines.push(`Pickup: ${startsAtLabel}`);
  if (fields.pickup_location) lines.push(`From: ${fields.pickup_location}`);
  if (fields.dropoff_location) lines.push(`To: ${fields.dropoff_location}`);
  if (fields.passenger_count) lines.push(`Passengers: ${fields.passenger_count}`);
  lines.push('Reply here if anything needs to change.');
  return lines.join('\n');
}
